import {
  Box,
  Button,
  ClientPaymentStatus,
  Disclaimer,
  Div,
  FinancesSaasPaymentEntity,
  formatAmount,
  H5,
  InfoComponentMobile,
  lightTheme,
  NumberType,
  P,
  Pill,
} from '@vgrubs/components';
import { DateTime } from 'luxon';
import {
  formatBilledPeriod,
  SAAS_STATUS_LABEL,
  saasStatusColor,
} from './saasDetails.helpers';

export const PAYMENT_COLUMNS = [
  { label: 'Location', width: '27%' },
  { label: 'Amount', width: '12%' },
  { label: 'Status', width: '12%' },
  { label: 'Billed period', width: '21%' },
  { label: 'Created', width: '12%' },
  { label: 'Retries', width: '12%' },
];

const [LOCATION, AMOUNT, STATUS, PERIOD, CREATED, RETRIES] =
  PAYMENT_COLUMNS.map((column) => column.width);

/** The statuses the Client Payments list offers a retry on. */
const RETRYABLE = [ClientPaymentStatus.failed, ClientPaymentStatus.chargedBack];

type Props = {
  payment: FinancesSaasPaymentEntity;
  onRetry: (payment: FinancesSaasPaymentEntity) => void;
};

/** One monthly SaaS payment: a table row on desktop, a card on a phone. */
export const SaasPaymentRow = ({ payment, onRetry }: Props) => {
  const openLocation = () =>
    window.open(
      `/locations/${payment.locationId}`,
      '_blank',
      'noopener,noreferrer'
    );
  const amount = formatAmount(payment.amount, NumberType.USD);
  const fee = payment.fee ? formatAmount(payment.fee, NumberType.USD) : null;
  // Drawn like the status pill on the deliveries list.
  const status = (
    <Pill $background={saasStatusColor(payment.status)}>
      <Disclaimer $m="0" $color={lightTheme.white} $semiBold $lineHeight="1">
        {SAAS_STATUS_LABEL[payment.status]}
      </Disclaimer>
    </Pill>
  );
  const period = formatBilledPeriod(payment.fromDate, payment.toDate);
  const created = DateTime.fromISO(payment.createdAt).toFormat('MMM dd, yyyy');
  const retry = RETRYABLE.includes(payment.status) && (
    <Button $selected $small $w="72px" onClick={() => onRetry(payment)}>
      Retry
    </Button>
  );

  return (
    <Div>
      {/* Gap and padding match the TableHeader above, so the columns line up. */}
      <Div
        $desktop
        $flex
        $alignCenter
        $gap="12px"
        $p="12px 16px"
        $borderBottom
        $borderColor={lightTheme.mediumGray}
      >
        <Div $width={LOCATION}>
          <P $m="0" $pointer $hoverUnderline onClick={openLocation}>
            {payment.locationName}
          </P>
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            {payment.description}
          </Disclaimer>
        </Div>
        <Div $width={AMOUNT}>
          <P $m="0">{amount}</P>
          {fee && (
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              Fee {fee}
            </Disclaimer>
          )}
        </Div>
        <Div $width={STATUS}>{status}</Div>
        <Div $width={PERIOD}>
          <P $m="0">{period}</P>
        </Div>
        <Div $width={CREATED}>
          <P $m="0">{created}</P>
        </Div>
        <Div $width={RETRIES} $flex $alignCenter $between $gap="8px">
          <P $m="0">{payment.retries}</P>
          {retry}
        </Div>
      </Div>

      <Box
        $mobile
        $flex
        $column
        $gap="4px"
        $mb="8"
        $p="12px 16px"
        $pSM="12px 16px"
        style={{ boxShadow: 'none' }}
      >
        <Div $flex $alignStart $between $gap="8px">
          <Div $minWidth="0">
            <H5 $m="0" $pointer onClick={openLocation}>
              {payment.locationName}
            </H5>
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              {payment.description}
            </Disclaimer>
          </Div>
          <H5 $m="0" $noWrap>
            {amount}
          </H5>
        </Div>
        <InfoComponentMobile title="Status" component={status} />
        {fee && (
          <InfoComponentMobile
            title="GoCardless fee"
            component={<Disclaimer $m="0">{fee}</Disclaimer>}
          />
        )}
        <InfoComponentMobile
          title="Billed period"
          component={<Disclaimer $m="0">{period}</Disclaimer>}
        />
        <InfoComponentMobile
          title="Created"
          component={<Disclaimer $m="0">{created}</Disclaimer>}
        />
        <InfoComponentMobile
          title="Retries"
          component={<Disclaimer $m="0">{payment.retries}</Disclaimer>}
        />
        {retry && (
          <Div $flex $column $mt="8">
            {retry}
          </Div>
        )}
      </Box>
    </Div>
  );
};
